import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, CreditCard, Eye, Wallet, FileText, Edit } from 'lucide-react';

const SupplierPayments = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';
  
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Filter states
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterType, setFilterType] = useState('');

  const [formData, setFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    supplier_id: '',
    payment_type: 'Invoice',
    purchase_id: '',
    po_reference: '',
    amount: '',
    payment_mode: 'Bank Transfer',
    reference_number: '',
    notes: ''
  });

  // Edit form state
  const [editFormData, setEditFormData] = useState({
    payment_date: '',
    amount: '',
    payment_mode: '',
    reference_number: '',
    notes: ''
  });

  const paymentModes = ['Cash', 'Bank Transfer', 'UPI', 'Cheque', 'NEFT', 'RTGS', 'IMPS'];

  const fetchPayments = useCallback(async () => {
    try {
      let url = '/supplier-payments/';
      const params = new URLSearchParams();
      if (filterSupplier) params.append('supplier_id', filterSupplier);
      if (filterType) params.append('payment_type', filterType);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await api.get(url);
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  }, [filterSupplier, filterType]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await api.get('/suppliers/');
      setSuppliers(response.data.filter(s => s.status === 'Active'));
    } catch (error) {
      console.error('Failed to fetch suppliers');
    }
  }, []);

  useEffect(() => {
    fetchPayments();
    fetchSuppliers();
  }, [fetchPayments, fetchSuppliers]);

  const fetchPendingInvoices = async (supplierId) => {
    if (!supplierId) {
      setPendingInvoices([]);
      return;
    }
    try {
      const response = await api.get(`/supplier-payments/pending-invoices/${supplierId}`);
      setPendingInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch pending invoices');
      setPendingInvoices([]);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      payment_date: new Date().toISOString().split('T')[0],
      supplier_id: '',
      payment_type: 'Invoice',
      purchase_id: '',
      po_reference: '',
      amount: '',
      payment_mode: 'Bank Transfer',
      reference_number: '',
      notes: ''
    });
    setPendingInvoices([]);
    setDialogOpen(true);
  };

  const handleViewPayment = async (payment) => {
    try {
      const response = await api.get(`/supplier-payments/${payment.id}`);
      setSelectedPayment(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load payment details');
    }
  };

  const handleSupplierChange = (supplierId) => {
    setFormData(prev => ({ 
      ...prev, 
      supplier_id: supplierId,
      purchase_id: '',
      amount: ''
    }));
    if (formData.payment_type === 'Invoice') {
      fetchPendingInvoices(supplierId);
    }
  };

  const handleTypeChange = (type) => {
    setFormData(prev => ({ 
      ...prev, 
      payment_type: type,
      purchase_id: '',
      po_reference: '',
      amount: ''
    }));
    if (type === 'Invoice' && formData.supplier_id) {
      fetchPendingInvoices(formData.supplier_id);
    }
  };

  const handleInvoiceChange = (purchaseId) => {
    const invoice = pendingInvoices.find(i => i.purchase_id === purchaseId);
    setFormData(prev => ({
      ...prev,
      purchase_id: purchaseId,
      amount: invoice?.pending_amount || ''
    }));
  };

  const getSelectedInvoice = () => {
    return pendingInvoices.find(i => i.purchase_id === formData.purchase_id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (formData.payment_type === 'Invoice' && !formData.purchase_id) {
      toast.error('Please select an invoice');
      return;
    }
    if (formData.payment_type === 'Advance' && !formData.po_reference) {
      toast.error('Please enter PO/Reference number');
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      params.append('payment_date', formData.payment_date);
      params.append('supplier_id', formData.supplier_id);
      params.append('payment_type', formData.payment_type);
      params.append('amount', formData.amount);
      params.append('payment_mode', formData.payment_mode);
      params.append('reference_number', formData.reference_number);
      params.append('notes', formData.notes);
      
      if (formData.payment_type === 'Invoice') {
        params.append('purchase_id', formData.purchase_id);
      } else {
        params.append('po_reference', formData.po_reference);
      }

      await api.post(`/supplier-payments/?${params.toString()}`);
      toast.success('Payment recorded successfully');
      setDialogOpen(false);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEditDialog = async (payment) => {
    try {
      const response = await api.get(`/supplier-payments/${payment.id}`);
      const paymentData = response.data;
      setSelectedPayment(paymentData);
      setEditFormData({
        payment_date: paymentData.payment_date || '',
        amount: paymentData.amount || '',
        payment_mode: paymentData.payment_mode || 'Bank Transfer',
        reference_number: paymentData.reference_number || '',
        notes: paymentData.notes || ''
      });
      setEditDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load payment details');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editFormData.amount || parseFloat(editFormData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      if (editFormData.payment_date) params.append('payment_date', editFormData.payment_date);
      params.append('amount', editFormData.amount);
      params.append('payment_mode', editFormData.payment_mode);
      params.append('reference_number', editFormData.reference_number);
      params.append('notes', editFormData.notes);

      await api.put(`/supplier-payments/${selectedPayment.id}?${params.toString()}`);
      toast.success('Payment updated successfully');
      setEditDialogOpen(false);
      setSelectedPayment(null);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payment');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredPayments = payments.filter(payment =>
    payment.payment_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payment.reference_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Supplier Payments</h1>
          <p className="text-gray-600">Release payments to suppliers against invoices</p>
        </div>
        <Button onClick={handleOpenDialog}>
          <Plus className="h-4 w-4 mr-2" />
          New Payment
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterSupplier || 'all'} onValueChange={(v) => setFilterSupplier(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Suppliers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType || 'all'} onValueChange={(v) => setFilterType(v === 'all' ? '' : v)}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Invoice">Invoice Payment</SelectItem>
                <SelectItem value="Advance">Advance Payment</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Payments ({filteredPayments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Payment #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.length > 0 ? (
                filteredPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell className="font-mono text-sm">{payment.payment_number}</TableCell>
                    <TableCell>{new Date(payment.payment_date).toLocaleDateString('en-IN')}</TableCell>
                    <TableCell>{payment.supplier_name}</TableCell>
                    <TableCell>
                      <Badge variant={payment.payment_type === 'Invoice' ? 'default' : 'secondary'}>
                        {payment.payment_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-medium">₹{payment.amount?.toLocaleString('en-IN')}</TableCell>
                    <TableCell>{payment.payment_mode}</TableCell>
                    <TableCell>{payment.reference_number || '-'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleViewPayment(payment)} title="View">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => handleOpenEditDialog(payment)} title="Edit">
                          <Edit className="h-4 w-4 text-blue-600" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                    No payments found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>New Supplier Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Date *</Label>
                  <Input
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Supplier *</Label>
                  <Select
                    value={formData.supplier_id || 'none'}
                    onValueChange={(v) => handleSupplierChange(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select Supplier</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Payment Type *</Label>
                <div className="flex gap-4 mt-2">
                  <Button
                    type="button"
                    variant={formData.payment_type === 'Invoice' ? 'default' : 'outline'}
                    onClick={() => handleTypeChange('Invoice')}
                    className="flex-1"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Against Invoice
                  </Button>
                  <Button
                    type="button"
                    variant={formData.payment_type === 'Advance' ? 'default' : 'outline'}
                    onClick={() => handleTypeChange('Advance')}
                    className="flex-1"
                  >
                    <Wallet className="h-4 w-4 mr-2" />
                    Advance Payment
                  </Button>
                </div>
              </div>

              {formData.payment_type === 'Invoice' && formData.supplier_id && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <Label>Select Invoice *</Label>
                  {pendingInvoices.length > 0 ? (
                    <>
                      <Select
                        value={formData.purchase_id || 'none'}
                        onValueChange={(v) => handleInvoiceChange(v === 'none' ? '' : v)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select pending invoice" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select pending invoice</SelectItem>
                          {pendingInvoices.map(inv => (
                            <SelectItem key={inv.purchase_id} value={inv.purchase_id}>
                              {inv.invoice_number} - Pending: ₹{inv.pending_amount?.toLocaleString('en-IN')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {getSelectedInvoice() && (
                        <div className="mt-3 p-3 bg-white rounded border">
                          <div className="grid grid-cols-3 gap-2 text-sm">
                            <div>
                              <span className="text-gray-500">Invoice Amount:</span>
                              <p className="font-medium">₹{getSelectedInvoice().net_amount?.toLocaleString('en-IN')}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Already Paid:</span>
                              <p className="font-medium text-green-600">₹{getSelectedInvoice().total_paid?.toLocaleString('en-IN')}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Pending:</span>
                              <p className="font-medium text-red-600">₹{getSelectedInvoice().pending_amount?.toLocaleString('en-IN')}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 mt-2">No pending invoices for this supplier</p>
                  )}
                </div>
              )}

              {formData.payment_type === 'Advance' && (
                <div>
                  <Label>PO / Reference Number *</Label>
                  <Input
                    value={formData.po_reference}
                    onChange={(e) => setFormData({ ...formData, po_reference: e.target.value })}
                    placeholder="Enter PO number or other reference"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Amount (₹) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                  />
                  {formData.payment_type === 'Invoice' && getSelectedInvoice() && (
                    <p className="text-xs text-gray-500 mt-1">
                      Balance after payment: ₹{(getSelectedInvoice().pending_amount - (parseFloat(formData.amount) || 0)).toLocaleString('en-IN')}
                    </p>
                  )}
                </div>
                <div>
                  <Label>Payment Mode *</Label>
                  <Select
                    value={formData.payment_mode}
                    onValueChange={(v) => setFormData({ ...formData, payment_mode: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentModes.map(mode => (
                        <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Reference Number</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                  placeholder="Transaction ID / Cheque No. / UTR"
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Processing...' : 'Record Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Payment Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment Details - {selectedPayment?.payment_number}</DialogTitle>
          </DialogHeader>
          {selectedPayment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Payment Date</Label>
                  <p className="font-medium">{new Date(selectedPayment.payment_date).toLocaleDateString('en-IN')}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Supplier</Label>
                  <p className="font-medium">{selectedPayment.supplier?.name}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Payment Type</Label>
                  <Badge variant={selectedPayment.payment_type === 'Invoice' ? 'default' : 'secondary'}>
                    {selectedPayment.payment_type}
                  </Badge>
                </div>
                <div>
                  <Label className="text-gray-500">Amount</Label>
                  <p className="text-xl font-bold text-green-600">₹{selectedPayment.amount?.toLocaleString('en-IN')}</p>
                </div>
              </div>
              {selectedPayment.payment_type === 'Invoice' && selectedPayment.purchase && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-gray-500">Invoice Details</Label>
                  <p className="font-medium">{selectedPayment.purchase.invoice_number}</p>
                  <p className="text-sm text-gray-500">Invoice Amount: ₹{selectedPayment.purchase.net_amount?.toLocaleString('en-IN')}</p>
                </div>
              )}
              {selectedPayment.payment_type === 'Advance' && (
                <div>
                  <Label className="text-gray-500">PO / Reference</Label>
                  <p className="font-medium">{selectedPayment.po_reference}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Payment Mode</Label>
                  <p className="font-medium">{selectedPayment.payment_mode}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Reference Number</Label>
                  <p className="font-medium">{selectedPayment.reference_number || '-'}</p>
                </div>
              </div>
              {selectedPayment.notes && (
                <div>
                  <Label className="text-gray-500">Notes</Label>
                  <p className="font-medium">{selectedPayment.notes}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog (Admin only) */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payment - {selectedPayment?.payment_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="space-y-4">
              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={editFormData.payment_date}
                  onChange={(e) => setEditFormData({ ...editFormData, payment_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={editFormData.amount}
                  onChange={(e) => setEditFormData({ ...editFormData, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Payment Mode *</Label>
                <Select
                  value={editFormData.payment_mode}
                  onValueChange={(v) => setEditFormData({ ...editFormData, payment_mode: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentModes.map(mode => (
                      <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference Number</Label>
                <Input
                  value={editFormData.reference_number}
                  onChange={(e) => setEditFormData({ ...editFormData, reference_number: e.target.value })}
                  placeholder="Transaction ID / Cheque No. / UTR"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  placeholder="Additional notes"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Updating...' : 'Update Payment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierPayments;
